import React, { useState, useRef, useEffect } from 'react';
import { Howl } from 'howler';
import { speakChinese } from '../utils/speechSynthesis';
import { API_URL } from '../config';

/**
 * AudioPlayer Component
 *
 * Plays audio files with a clean icon-based interface
 * Uses Howler.js for reliable cross-browser audio playback
 * Falls back to Web Speech API for Chinese text-to-speech
 *
 * Props:
 * @param {string} src - Audio file path (optional if text provided)
 * @param {string} text - Chinese text for TTS fallback (optional)
 * @param {boolean} autoPlay - Whether to play automatically on mount (default: false)
 * @param {string} className - Additional CSS classes
 */
function AudioPlayer({ src, text, autoPlay = false, className = '' }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const soundRef = useRef(null);

  // Audio uploaded via the admin form is served from the backend at /audio/…
  // The browser resolves that path against the current origin (the SPA host),
  // not the API host, so we prepend API_URL for relative paths. Absolute URLs
  // and data: URIs (legacy base64 audio) pass through unchanged.
  const resolvedSrc = (() => {
    if (!src) return null;
    if (src.startsWith('http://') || src.startsWith('https://') || src.startsWith('data:') || src.startsWith('blob:')) {
      return src;
    }
    if (src.startsWith('/')) return `${API_URL}${src}`;
    return src;
  })();

  // Initialize Howl instance
  useEffect(() => {
    if (!resolvedSrc) {
      // If no audio file but we have text, don't set error - just skip audio loading
      if (!text) {
        setHasError(true);
      }
      return;
    }

    // Create new Howl instance
    const sound = new Howl({
      src: [resolvedSrc],
      html5: true, // Use HTML5 Audio for better mobile support
      onload: () => {
        setIsLoading(false);
        setHasError(false);
        if (autoPlay) {
          sound.play();
          setIsPlaying(true);
        }
      },
      onloaderror: () => {
        setIsLoading(false);
        setHasError(true);
        console.warn(`Failed to load audio: ${src}`);
      },
      onplay: () => {
        setIsPlaying(true);
      },
      onend: () => {
        setIsPlaying(false);
      },
      onpause: () => {
        setIsPlaying(false);
      },
      onstop: () => {
        setIsPlaying(false);
      },
    });

    soundRef.current = sound;
    setIsLoading(true);

    // Cleanup on unmount
    return () => {
      if (soundRef.current) {
        soundRef.current.unload();
      }
    };
  }, [resolvedSrc, autoPlay]);

  const handlePlay = async (e) => {
    // Prevent card flip when clicking audio button
    e.stopPropagation();

    // If we have a sound file and it's loaded, use it
    if (soundRef.current && !hasError) {
      if (isPlaying) {
        soundRef.current.pause();
      } else {
        soundRef.current.play();
      }
      return;
    }

    // Fallback to text-to-speech if audio file failed/doesn't exist and we have text
    if (text && (hasError || !src)) {
      try {
        setIsPlaying(true);
        await speakChinese(text);
        setIsPlaying(false);
      } catch (error) {
        console.warn('Failed to speak text:', error);
        setIsPlaying(false);
      }
    }
  };

  // Don't render if no source and no text
  if (!src && !text) return null;

  return (
    <button
      className={`audio-player ${className} ${hasError && !text ? 'audio-error' : ''}`}
      onClick={handlePlay}
      disabled={isLoading || (hasError && !text)}
      aria-label={isPlaying ? 'Pause audio' : 'Play audio'}
      title={text && hasError ? 'Play text-to-speech' : (isPlaying ? 'Pause' : 'Play')}
    >
      {isLoading && (
        <svg className="audio-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <circle cx="12" cy="12" r="10" strokeWidth="2" opacity="0.25" />
          <path
            d="M12 2 A10 10 0 0 1 22 12"
            strokeWidth="2"
            strokeLinecap="round"
            className="spinner"
          />
        </svg>
      )}

      {!isLoading && !hasError && !isPlaying && (
        <svg className="audio-icon" viewBox="0 0 24 24" fill="currentColor">
          <path d="M8 5v14l11-7z" />
        </svg>
      )}

      {!isLoading && !hasError && isPlaying && (
        <svg className="audio-icon" viewBox="0 0 24 24" fill="currentColor">
          <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
        </svg>
      )}

      {hasError && text && (
        <svg className="audio-icon" viewBox="0 0 24 24" fill="currentColor">
          <path d="M8 5v14l11-7z" />
        </svg>
      )}

      {hasError && !text && (
        <svg className="audio-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M18.364 5.636a9 9 0 010 12.728m-2.121-10.607a5.5 5.5 0 010 7.778M12 12h.01"
          />
        </svg>
      )}
    </button>
  );
}

export default AudioPlayer;
